<template>
  <div class="summary-bar-selection-mobile" :class="{ 'is-collapsed': isCollapsed && !isEditing }">
    <header class="summary-bar-selection-mobile__header">
      <div class="summary-bar-selection-mobile__header-inner">
        <button
          type="button"
          class="summary-bar-header-clickable-btn"
          data-testid="summary-bar-header-clickable"
          aria-label="Toggle selection details"
          @click="handleHeaderClick"
        ></button>
        <div class="summary-bar-selection-mobile__header-row">
          <Transition mode="out-in" name="fade">
            <div v-if="isTotalsMode" :key="DISPLAY_MODES.TOTALS" class="totals-info" data-testid="summary-bar-totals">
              <span class="price">{{ estimatedTotal }}</span>
              <span class="dot" aria-hidden="true">・</span>
              <span class="items-count">{{ totalQuantity }} item{{ pluralize(totalQuantity) }}</span>
            </div>
            <div v-else :key="DISPLAY_MODES.LABEL" class="totals-info large" data-testid="summary-bar-label">
              <span class="price">Estimated total</span>
            </div>
          </Transition>

          <div class="action-menu" data-testid="summary-bar-menu" @click.stop @keydown.stop>
            <Icon :icon="csgMoreIos" size="m" />
            <select
              id="summary-bar-options"
              class="action-select"
              name="summary-bar-options"
              aria-label="Edit selected items options"
              :disabled="isLoading"
              @change="handleMenuChange"
            >
              <option value="" disabled selected hidden>Edit</option>
              <option :value="MENU_ACTIONS.EDIT">Remove select items</option>
              <option :value="MENU_ACTIONS.REMOVE_ALL">Remove all</option>
            </select>
          </div>
        </div>

        <div v-if="globalErrors.length" class="summary-bar-selection-mobile__error" data-testid="summary-bar-selection-error">
          {{ globalErrors[0] }}
        </div>
      </div>
    </header>

    <div class="summary-bar-selection-mobile__carousel" data-testid="summary-bar-carousel">
      <SummaryBarCarousel
        class="summary-carousel--selection-mobile"
        :list="selectedItems"
        :gap-mobile="8"
        :visible-slides-mobile="4.5"
        @item-click="handleCarouselItemClick"
      >
        <template #item="{ data: item }">
          <div class="carousel-item">
            <Picture
              v-if="item.imageUrl"
              :key="item.upcId"
              :lazy-load="false"
              class="item-image"
              :image-url="item.imageUrl"
              :alt="item.alt || ''"
              :image-sizes-by-breakpoints="imageSizesByBreakpoints"
            />

            <SummaryBarItemBadge
              :quantity="item.quantity"
              :error-message="failedUpcs?.[item.upcId]?.[0]"
              :is-removable="isEditing"
              @remove="removeItem(item)"
            />
          </div>
        </template>
      </SummaryBarCarousel>
    </div>

    <footer class="summary-bar-selection-mobile__footer">
      <template v-if="isEditing">
        <BusyButton variant="primary" expanded data-testid="summary-bar-done" @click="handleFinishEditing"> Done </BusyButton>
      </template>
      <template v-else>
        <BusyButton
          variant="secondary"
          expanded
          :disabled="!hasSelections || isLoading"
          :is-loading="isBuyNowLoading"
          data-testid="summary-bar-buy-now"
          @click="onBuyNow"
        >
          Buy Now
        </BusyButton>
        <BusyButton
          variant="emphasis"
          expanded
          :disabled="!hasSelections || isLoading"
          :is-loading="isAddToBagLoading"
          data-testid="summary-bar-add-to-bag"
          @click="onAddToBag"
        >
          Add to Bag
        </BusyButton>
      </template>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { csgMoreIos } from '@csgSvgIcons/icons';

import Picture from '@atomic-ui/picture/Picture';
import Icon from '@atomic-ui/icon/Icon.vue';
import BusyButton from '~/modules/ui/components/BusyButton.vue';
import SummaryBarCarousel from './SummaryBarCarousel.vue';
import SummaryBarItemBadge from './SummaryBarItemBadge.vue';

import { useCollectionSelectionStore, type SelectedItem } from '~/stores/useCollectionSelectionStore';
import { useShopTheCollectionUIStore } from '~/stores/useShopTheCollectionUIStore';
import { useMultiATBStore } from '~/stores/useMultiATBStore';

import { tagSTCClearAll } from '~/features/analytics/shopTheCollection';

import { pluralize } from '~/utils/string';

import { useSummaryBarActions } from '../composables/useSummaryBarActions';

// Constants
const MENU_ACTIONS = {
  EDIT: 'edit',
  REMOVE_ALL: 'remove-all',
} as const;

const DISPLAY_MODES = {
  TOTALS: 'totals',
  LABEL: 'label',
} as const;

const DISPLAY_LABEL_DELAY_MS = 3000;
const DISPLAY_TOTALS_DELAY_MS = 6000; // 3s + 3s
const imageSizesByBreakpoints = { small: 75, medium: 75, large: 75, mlarge: 75, xlarge: 75 };

defineProps<{
  isCollapsed: boolean;
}>();

const emit = defineEmits<{
  (e: 'success' | 'toggle-collapse'): void;
  (e: 'remove-all', items: SelectedItem[]): void;
}>();

const selectionStore = useCollectionSelectionStore();
const stcUIStore = useShopTheCollectionUIStore();
const multiATBStore = useMultiATBStore();

const { totalQuantity, selections, estimatedTotal, hasSelections } = storeToRefs(selectionStore);
const { globalErrors, failedUpcs, isLoading, isAddToBagLoading, isBuyNowLoading } = storeToRefs(multiATBStore);

const { onAddToBag, onBuyNow } = useSummaryBarActions(() => {
  emit('success');
});

const isEditing = ref(false);
const displayTextMode = ref<(typeof DISPLAY_MODES)[keyof typeof DISPLAY_MODES]>(DISPLAY_MODES.TOTALS);

const isTotalsMode = computed(() => displayTextMode.value === DISPLAY_MODES.TOTALS);

let animationTimer1: ReturnType<typeof setTimeout> | null = null;
let animationTimer2: ReturnType<typeof setTimeout> | null = null;

const clearTimers = () => {
  if (animationTimer1) clearTimeout(animationTimer1);
  if (animationTimer2) clearTimeout(animationTimer2);
};

onUnmounted(clearTimers);

watch(totalQuantity, () => {
  // Micro-animation logic
  clearTimers();
  displayTextMode.value = DISPLAY_MODES.TOTALS;

  animationTimer1 = setTimeout(() => {
    displayTextMode.value = DISPLAY_MODES.LABEL;
  }, DISPLAY_LABEL_DELAY_MS);

  animationTimer2 = setTimeout(() => {
    displayTextMode.value = DISPLAY_MODES.TOTALS;
  }, DISPLAY_TOTALS_DELAY_MS);
});

const selectedItems = computed<SelectedItem[]>(() => {
  return Object.values(selections.value).filter((item: SelectedItem) => item.quantity > 0);
});

const handleMenuChange = (event: Event) => {
  if (isLoading.value) return;
  const target = event.target as HTMLSelectElement;
  const value = target.value;

  if (value === MENU_ACTIONS.EDIT) {
    isEditing.value = true;
  } else if (value === MENU_ACTIONS.REMOVE_ALL) {
    tagSTCClearAll();
    const savedSelections = Object.values(selections.value).filter((item: SelectedItem) => item.quantity > 0);

    selectionStore.clearSelection(true);
    isEditing.value = false;

    if (savedSelections.length > 0) {
      emit('remove-all', savedSelections);
    }
  }

  // Reset select value to allow selecting the same option again
  target.value = '';
};

const handleCarouselItemClick = ({ item }: { item: SelectedItem }) => {
  if (isLoading.value) return;
  if (!isEditing.value) {
    stcUIStore.openBottomSheet(item.productId, item.upcId);
  }
};

const handleHeaderClick = () => {
  if (!isEditing.value) {
    emit('toggle-collapse');
  }
};

const handleFinishEditing = () => {
  isEditing.value = false;
};

const removeItem = (item: SelectedItem) => {
  if (isLoading.value) return;
  selectionStore.updateSelection(item.productId, item.upcId, { quantity: 0 });
  if (selectedItems.value.length === 0) {
    isEditing.value = false;
  }
};
</script>






<style lang="scss" scoped>
@import '~settingSCSS';

.summary-bar-selection-mobile {
  &.is-collapsed {
    .summary-bar-selection-mobile__carousel {
      max-height: 0;
      opacity: 0;
    }

    .summary-bar-selection-mobile__footer {
      @include padding-top-z;
    }
  }
}

.summary-bar-selection-mobile__header {
  @include padding-left-xs;
  @include padding-right-xs;

  .summary-bar-selection-mobile__header-inner {
    position: relative;
  }

  .summary-bar-selection-mobile__header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    pointer-events: none;
  }

  .totals-info {
    display: flex;
    align-items: center;

    .dot {
      margin: 0 4px;
      color: $black;
    }

    .price,
    .items-count {
      @include body(large);
      line-height: 22px;
    }
  }

  .action-menu {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 32px;
    height: 32px;
    z-index: 5;
    pointer-events: auto;

    &:focus-within {
      outline: auto;
    }

    .action-select {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      z-index: 5;
    }
  }
}

.summary-bar-header-clickable-btn {
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  outline: none;
  position: absolute;
  inset: 0;
  z-index: 1;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px $black;
  }
}

.summary-bar-selection-mobile__error {
  color: $error-color;
  margin-top: rem-calc(4);
}

.summary-bar-selection-mobile__carousel {
  max-height: 120px;
  opacity: 1;
  overflow: hidden;
  transition:
    max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease;

  :deep(.carousel-item) {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 5;
    background-color: $white;

    .item-image {
      width: 100%;
      height: 100%;
      :deep(img) {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }
}

.summary-bar-selection-mobile__footer {
  display: flex;
  gap: 12px;
  width: 100%;
  @include padding-top-xs;
  @include padding-left-xs;
  @include padding-right-xs;
  @include padding-bottom-xxs;
  position: relative;
  z-index: 2;
  transition: padding-top 0.3s ease;
}


@import '~settingSCSS';

.summary-bar-selection-mobile__header {
  padding-top: 14px;
  padding-bottom: 14px;
}
</style>